import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type LiveTvChannel } from '@/types/livetv';

interface Props {
  channel: LiveTvChannel;
}

/** Fallback tile when a channel has no logo (all daddylive channels, and iptv
 *  channels with no logo): a generated monogram (first 1-2 significant chars). */
function monogram(name: string): string {
  const words = name.replace(/[^\w\s]/g, ' ').trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export function LiveTvChannelCard({ channel }: Props): JSX.Element {
  const { t } = useTranslation();
  // iptv-org logos are third-party and occasionally 404 / hotlink-block; fall
  // back to the monogram on load error so we never render a broken image.
  const [logoBroken, setLogoBroken] = useState(false);
  const showLogo = Boolean(channel.logo) && !logoBroken;
  return (
    <Link
      to={`/watch/live-tv/${encodeURIComponent(channel.id)}`}
      className="livetv-card"
      data-testid="livetv-card"
      data-channel-id={channel.id}
    >
      <div className={`livetv-card__logo livetv-card__logo--${channel.category}`} aria-hidden>
        {showLogo ? (
          <img
            className="livetv-card__logo-img"
            src={channel.logo as string}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setLogoBroken(true)}
          />
        ) : (
          <span>{monogram(channel.name)}</span>
        )}
        <span className="livetv-card__live">{t('livetv.live')}</span>
      </div>
      <div className="livetv-card__body">
        <div className="livetv-card__name" title={channel.name}>{channel.name}</div>
        <div className="livetv-card__meta">
          {t(`livetv.categories.${channel.category}`)}
          {channel.country ? ` · ${channel.country}` : ''}
        </div>
      </div>
    </Link>
  );
}
